import { useNavigate } from "react-router";
import { useState } from "react";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import PageMeta from "../common/PageMeta";
import { useModal } from "../../hooks/useModal";
import Modal from "../common/Modal";
import api from "../../services/api";
import Alert from '../ui/alert/Alert';
import { usePermissions } from "../../hooks/usePermissions";
import { generateRefundReceipt } from "../../utils/generateRefundReceipt";
import EbookDeliveryCard from "../ebook/EbookDeliveryCard";

interface TermAcceptance {
    id: string;
    title: string;
    version: number;
    acceptedAt: string;
    ip: string;
    userAgent: string;
    customerEmail: string;
}

interface SaleParticipant {
    companyId: string;
    companyName: string;
    role: string;
    percentage: number;
    amount: number;
}

interface ProductItem {
    id: string;
    productName: string;
    productImage?: string;
    offerName: string;
    price: number;
    originalPrice: number;
    quantity: number;
    totalPrice: number;
    avatar?: string;
}

interface SaleData {
    transaction: {
        id: string;
        transactionId: string;
        status: string;
        createdAt: string;
        updatedAt: string;
        paidAt?: string;
        saleValue: number;
    };
    customer: {
        id: string;
        name: string;
        email: string;
        document: string;
        phone: string;
        avatar?: string;
    };
    order: {
        id?: string;
        totalAmount: string | number;
        terms?: TermAcceptance[];
    };
    product: {
        items: ProductItem[];
    };
    payment: {
        method: string;
        grossAmount: number;
        transactionDate: string;
        installments: number;
        firstDigits?: string;
        lastDigits?: string;
        expirationDate?: string;
        cardholderName?: string;
        netAmount: number;
        refusalMessage?: string;
        estimatedReleaseDate?: string;
        gateway?: string;
    };
    financial: {
        grossValue: number;
        surchargeValue: number;
        transactionFee: number;
        netValue: number;
        splitRules: unknown[];
        releaseForecast: string | null;
    };
    coProducer: { amount: number; [key: string]: unknown } | null;
    saleParticipants: SaleParticipant[];
    tracking: {
        source: string;
        medium: string;
        campaign: string;
        content: string;
        term: string;
        sck: string;
    };
}

interface SalesDetailsProps {
    saleData?: SaleData;
    isLoading?: boolean;
}

export default function SalesDetails({ saleData }: SalesDetailsProps) {
    const { hasPermission } = usePermissions();
    const { isOpen, openModal, closeModal } = useModal();
    const [isRefunding, setIsRefunding] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");
    const [notificationType, setNotificationType] = useState<"success" | "error">("success");

    // Função para mostrar notificação
    const displayNotification = (message: string, type: "success" | "error") => {
        setNotificationMessage(message);
        setNotificationType(type);
        setShowNotification(true);

        // Auto-esconder após 5 segundos
        setTimeout(() => {
            setShowNotification(false);
        }, 5000);
    };

    // Função para gerar comprovante de reembolso
    const handleGenerateRefundReceipt = async () => {
        if (!saleData) return;

        await generateRefundReceipt({
            customerName: saleData.customer?.name || 'Cliente',
            customerEmail: saleData.customer?.email || '',
            customerDocument: saleData.customer?.document,
            transactionId: saleData.transaction?.id || '',
            paymentDate: saleData.transaction?.paidAt || saleData.transaction?.createdAt || '',
            paymentAmount: saleData.transaction?.saleValue || 0,
            orderAmount: Number(saleData.order?.totalAmount) || saleData.transaction?.saleValue || 0,
            refundAmount: Number(saleData.order?.totalAmount) || saleData.transaction?.saleValue || 0,
            refundDate: saleData.transaction?.updatedAt || new Date().toISOString(),
            paymentMethod: saleData.payment?.method || '',
            lastDigits: saleData.payment?.lastDigits,
            cardBrand: saleData.payment?.firstDigits,
            installments: saleData.payment?.installments,
            productName: saleData.product?.items?.[0]?.productName
        });
    };

    // Função para processar o reembolso
    const handleRefund = async () => {
        if (!saleData?.transaction?.id) {
            displayNotification('ID da transação não encontrado', 'error');
            return;
        }

        setIsRefunding(true);
        try {
            await api.delete(`/api/sales/refund/${saleData.transaction.id}`);
            displayNotification('Reembolso processado com sucesso!', 'success');
            closeModal();
            // Aqui você pode adicionar lógica para atualizar os dados da venda
            window.location.reload(); // Recarrega a página para atualizar os dados
        } catch (error: unknown) {
            console.error('Erro ao processar reembolso:', error);
            const axiosErr = error as { response?: { data?: { message?: string } } };
            const errorMessage = axiosErr.response?.data?.message || 'Erro ao processar reembolso';
            displayNotification(errorMessage, 'error');
        } finally {
            setIsRefunding(false);
        }
    };

    // Formatação de valores numéricos para exibição
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    // Formatação de data
    const formatDate = (dateStr: string, showTime: boolean = false) => {
        if (!dateStr) return '--';

        const date = new Date(dateStr);
        const options: Intl.DateTimeFormatOptions = {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            ...(showTime && { hour: '2-digit', minute: '2-digit' })
        };

        return new Intl.DateTimeFormat('pt-BR', options).format(date);
    };

    // Funções utilitárias para formatação
    const formatDocument = (doc: string | null | undefined): string => {
        if (!doc) return '--';
        // Remove todos os caracteres não numéricos
        const numbers = doc.replace(/\D/g, '');

        // Verifica se é CPF ou CNPJ pelo número de dígitos
        if (numbers.length === 11) {
            // Formato CPF: 000.000.000-00
            return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        } else if (numbers.length === 14) {
            // Formato CNPJ: 00.000.000/0000-00
            return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
        }
        return doc;
    };

    const formatPhone = (phone: string | null | undefined): string => {
        if (!phone) return '--';
        // Remove todos os caracteres não numéricos e o prefixo +55 se existir
        const numbers = phone.replace(/^\+55/, '').replace(/\D/g, '');

        // Verifica se tem 11 dígitos (com DDD e 9) ou 10 dígitos (com DDD sem 9)
        if (numbers.length === 11) {
            return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        } else if (numbers.length === 10) {
            return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        }
        return phone;
    };

    const getWhatsAppLink = (phone: string | null | undefined): string => {
        if (!phone) return '#';
        // Remove tudo exceto números e mantém o código do país se existir
        const numbers = phone.replace(/\D/g, '');
        // Adiciona o código do país (55) se não existir
        const fullNumber = numbers.startsWith('55') ? numbers : `55${numbers}`;
        return `https://wa.me/${fullNumber}`;
    };

    // Helper para status do pagamento
    const getStatusBadge = (status: string) => {
        const statusMap: Record<string, { text: string, color: string }> = {
            'pending': { text: 'Pendente', color: 'yellow' },
            'approved': { text: 'Aprovado', color: 'green' },
            'rejected': { text: 'Recusado', color: 'red' },
            'declined': { text: 'Recusado', color: 'red' },
            'refunded': { text: 'Reembolsado', color: 'gray' },
            'waiting_payment': { text: 'Aguardando Pagamento', color: 'blue' },
            'cancelled': { text: 'Cancelado', color: 'red' },
            'canceled': { text: 'Cancelado', color: 'red' },
            'overdue': { text: 'Vencida', color: 'yellow' },
            'expired': { text: 'Expirada', color: 'yellow' },
        };

        const { text, color } = statusMap[status] || { text: status, color: 'gray' };

        const colorClasses: Record<string, { bg: string, text: string }> = {
            green: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
            red: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
            yellow: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400' },
            blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
            gray: { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-400' },
        };

        return (
            <div className={`px-3 py-1 text-sm ${colorClasses[color].text} ${colorClasses[color].bg} rounded-full inline-flex items-center`}>
                <span className="mr-1">●</span> {text}
            </div>
        );
    };

    const navigate = useNavigate();

    // Se ainda não temos dados, podemos mostrar um estado vazio ou um placeholder
    if (!saleData) {
        return (
            <>
                <PageMeta
                    title="Sellyo - Detalhes da Venda"
                    description="Detalhes da venda"
                />
                <PageBreadcrumb
                    pageTitle="Detalhes da Venda"
                    showBackButton={true}
                    onBack={() => navigate('/sales')}
                />
                <div className="text-center p-8">
                    Nenhum dado disponível
                </div>
            </>
        );
    }

    return (
        <>
            <PageMeta
                title="Sellyo - Detalhes da Venda"
                description="Detalhes da venda"
            />

            <PageBreadcrumb
                pageTitle="Detalhes da Venda"
                showBackButton={true}
                onBack={() => navigate('/sales')}
            />

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Coluna principal com dados da transação e cliente */}
                <div className="w-full lg:w-2/3 flex flex-col gap-6">
                    {/* Bloco de dados do cliente */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                        <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400 mb-4">CLIENTE</h3>

                        <div className="flex items-center mb-6">
                            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-xl mr-3 dark:bg-blue-900/30 dark:text-blue-400">
                                {saleData.customer.avatar || saleData.customer.name?.[0]?.toUpperCase() || "C"}
                            </div>
                            <div>
                                <p className="text-base font-medium text-gray-900 dark:text-white">{saleData.customer.name}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{saleData.customer.email}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">CPF/CNPJ</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{formatDocument(saleData.customer.document)}</p>
                            </div>
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">TELEFONE</h3>
                                <div className="mt-1 flex items-center gap-2">
                                    <p className="text-sm text-gray-700 dark:text-gray-300">
                                        {formatPhone(saleData.customer.phone)}
                                    </p>
                                    {saleData.customer.phone && (
                                        <a
                                            href={getWhatsAppLink(saleData.customer.phone)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center"
                                            title="Abrir conversa no WhatsApp"
                                        >
                                            <img
                                                src="/images/brand/whatsapp.webp"
                                                alt="WhatsApp"
                                                className="h-5 w-5 hover:opacity-80 transition-opacity"
                                            />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Termos de uso aceitos */}
                        {saleData.order?.terms && saleData.order.terms.length > 0 && (
                            <div className="mt-4">
                                {saleData.order.terms.map((term: { id: string; title: string; version: number; acceptedAt: string; ip: string; userAgent: string }) => (
                                        <div key={term.id} className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                                            <div className="flex items-center gap-2 mb-3">
                                                <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                                </svg>
                                                <span className="text-sm font-medium">
                                                    {term.title} <span className="font-normal">(v{term.version})</span>
                                                </span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                                <div>
                                                    <span className="font-medium">Aceito em:</span>
                                                    <p className="mt-0.5">{new Date(term.acceptedAt).toLocaleString('pt-BR')}</p>
                                                </div>
                                                <div>
                                                    <span className="font-medium">Endereço IP:</span>
                                                    <p className="mt-0.5">{term.ip}</p>
                                                </div>
                                            </div>
                                            <div className="mt-2 text-xs">
                                                <span className="font-medium">Dispositivo:</span>
                                                <p className="mt-0.5 break-all">{term.userAgent || 'Desconhecido'}</p>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                    {/* Entrega do e-book por e-mail (só aparece se o pedido tem e-book) */}
                    {saleData.order?.id && <EbookDeliveryCard orderId={saleData.order.id} />}
                    {/* Bloco de dados principais da transação */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                        <div className="mb-4 flex justify-between">
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400">VALOR DA VENDA</h3>
                                <p className="mt-3 text-2xl font-bold text-gray-900 dark:text-white">
                                    {formatCurrency(saleData.transaction.saleValue)}
                                </p>
                            </div>
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400">STATUS</h3>
                                <div className="mt-3">
                                    {getStatusBadge(saleData.transaction.status)}
                                </div>
                            </div>
                        </div>

                        {/* Alerta de recusa */}
                        {(saleData.transaction.status === 'rejected' || saleData.transaction.status === 'declined') &&
                            saleData.payment?.refusalMessage && (
                                <div className="mb-4">
                                    <Alert
                                        variant="error"
                                        title="Transação Recusada"
                                        message={saleData.payment.refusalMessage}
                                    />
                                </div>
                            )}

                        {/* Alerta de cancelamento */}
                        {(saleData.transaction.status === 'cancelled' || saleData.transaction.status === 'canceled') &&
                            saleData.payment?.refusalMessage && (
                                <div className="mb-4">
                                    <Alert
                                        variant="error"
                                        title="Transação Cancelada"
                                        message={saleData.payment.refusalMessage}
                                    />
                                </div>
                            )}

                        <hr className="my-8 border-gray-300 dark:border-gray-700" />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">ID DA TRANSAÇÃO</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{saleData.transaction.id.toUpperCase()}</p>
                            </div>
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">CRIADA EM</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{formatDate(saleData.transaction.createdAt, true)}</p>
                            </div>
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">ÚLTIMA ATUALIZAÇÃO</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{formatDate(saleData.transaction.updatedAt, true)}</p>
                            </div>
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">PAGO EM</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{formatDate(saleData.transaction.paidAt || '', true)}</p>
                            </div>
                        </div>

                        <hr className="my-8 border-gray-300 dark:border-gray-700" />

                        <div className="space-y-4">
                            <div className="flex justify-between mb-3">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Valor Bruto</p>
                                <p className="text-sm text-gray-900 dark:text-white">{formatCurrency(saleData.financial.grossValue)}</p>
                            </div>

                            {Array.isArray(saleData.saleParticipants) && saleData.saleParticipants.length > 0 && (
                                <div className="mt-3">
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                                        Divisão de valores:
                                    </p>

                                    <div className="space-y-2">
                                        {saleData.saleParticipants.map((participant: SaleParticipant) => (
                                            <div
                                                key={participant.companyId}
                                                className="flex justify-between items-center ml-2"
                                            >
                                                <span className="text-sm text-gray-900 dark:text-white">
                                                    {participant.companyName}
                                                </span>

                                                <span className="text-sm text-gray-900 dark:text-white">
                                                    {formatCurrency(Number(participant.amount))}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {saleData.payment.installments > 1 && (
                                <div className="flex justify-between mb-3">
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Valor com acréscimo</p>
                                    <p className="text-sm text-gray-900 dark:text-white">{formatCurrency(Number(saleData.order.totalAmount))}</p>
                                </div>
                            )}


                            <div className="flex justify-between mb-3">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Taxa Intermediação</p>
                                <p className="text-sm text-gray-900 dark:text-white">{formatCurrency(saleData.financial.transactionFee)}</p>
                            </div>


                            <div className="flex justify-between mb-3">
                                <p className="text-sm text-gray-600 dark:text-gray-400">Data de liberação estimada</p>
                                <p className="text-sm text-gray-900 dark:text-white">{formatDate(saleData.payment.estimatedReleaseDate || '') || '--'}</p>
                            </div>

                            <div className="flex justify-between mb-3">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">Valor líquido</p>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">aprox.</span>

                                    {saleData.coProducer
                                        ? formatCurrency(saleData.coProducer.amount)
                                        : formatCurrency(saleData.financial.netValue)}
                                </p>
                            </div>
                        </div>


                        {saleData.transaction.status === 'approved' && hasPermission('sales.refund') && (
                            <div>
                                <hr className="my-8 border-gray-300 dark:border-gray-700" />
                                <div className="mt-6 flex justify-end">
                                    <button
                                        onClick={openModal}
                                        className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 dark:bg-transparent dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-800"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        Reembolsar
                                    </button>
                                </div>
                            </div>
                        )}

                        {saleData.transaction.status === 'refunded' && (
                            <div>
                                <hr className="my-8 border-gray-300 dark:border-gray-700" />
                                <div className="mt-6 flex justify-end">
                                    <button
                                        onClick={handleGenerateRefundReceipt}
                                        className="px-4 py-2 text-sm text-white bg-indigo-600 border border-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-2 transition-colors shadow-sm"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        Comprovante de Reembolso
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Coluna lateral com detalhes do carrinho, pagamento e taxas */}
                <div className="w-full lg:w-1/3 flex flex-col gap-6">
                    {/* Bloco de produtos */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                        <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400 mb-4">PRODUTO</h3>

                        {/* Produto principal */}
                        {saleData.product.items && saleData.product.items.map((item: ProductItem, index: number) => (
                            <div key={index} className="flex items-center mb-4">
                                <div className="flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-700 rounded-xl mr-3 dark:bg-blue-900/30 dark:text-blue-400">
                                    {item.productImage ? (
                                        <img
                                            src={item.productImage}
                                            alt={item.productName}
                                            className="w-10 h-10 object-cover rounded"
                                            onError={(e) => {
                                                // Fallback para texto em caso de erro na imagem
                                                e.currentTarget.style.display = 'none';
                                                e.currentTarget.parentElement!.textContent = item.productName?.[0]?.toUpperCase() || "P";
                                            }}
                                        />
                                    ) : (
                                        // Fallback para a primeira letra se não houver imagem
                                        item.productName?.[0]?.toUpperCase() || "P"
                                    )}
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{item.productName}</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {formatCurrency(item.price)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Bloco de pagamento */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                        <div className="mb-6">
                            <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400 mb-2">PAGAMENTO</h3>
                            <div className="flex items-center">
                                {(() => {
                                    switch (saleData.payment.method) {
                                        case 'card': {
                                            // Identifica a bandeira do cartão com base nos primeiros dígitos
                                            const firstDigits = saleData.payment.firstDigits || '';

                                            // Testa as principais bandeiras
                                            if (firstDigits.startsWith('4')) {
                                                return <img src="/images/payment/visa.svg" alt="Visa" className="h-6" />;
                                            } else if (
                                                firstDigits.startsWith('5') && parseInt(firstDigits[1]) >= 1 && parseInt(firstDigits[1]) <= 5 ||
                                                (firstDigits.startsWith('2') && parseInt(firstDigits) >= 2221 && parseInt(firstDigits) <= 2720)
                                            ) {
                                                return <img src="/images/payment/mastercard.svg" alt="Mastercard" className="h-6" />;
                                            } else if (firstDigits.startsWith('34') || firstDigits.startsWith('37')) {
                                                return <img src="/images/payment/amex.svg" alt="American Express" className="h-6" />;
                                            } else if (
                                                firstDigits.startsWith('36') ||
                                                firstDigits.startsWith('38') ||
                                                firstDigits.startsWith('39') ||
                                                (firstDigits.startsWith('30') && parseInt(firstDigits[2]) >= 0 && parseInt(firstDigits[2]) <= 5)
                                            ) {
                                                return <img src="/images/payment/diners.svg" alt="Diners Club" className="h-6" />;
                                            } else if (
                                                firstDigits.startsWith('6011') ||
                                                (firstDigits.startsWith('65')) ||
                                                (parseInt(firstDigits.substring(0, 3)) >= 644 && parseInt(firstDigits.substring(0, 3)) <= 649)
                                            ) {
                                                return <img src="/images/payment/discover.svg" alt="Discover" className="h-6" />;
                                            } else if (
                                                // Alguns BINs comuns da Elo
                                                ['401178', '401179', '438935', '457631', '457632', '504175', '627780', '636297', '636368'].some(prefix =>
                                                    firstDigits.startsWith(prefix)
                                                ) ||
                                                (firstDigits.startsWith('50') && ['6699', '6770', '6771', '6772', '6773'].some(mid =>
                                                    firstDigits.startsWith(`50${mid}`)
                                                )) ||
                                                (firstDigits.startsWith('509'))
                                            ) {
                                                return <img src="/images/payment/elo.svg" alt="Elo" className="h-6" />;
                                            } else if (firstDigits.startsWith('606282')) {
                                                return <img src="/images/payment/hipercard.svg" alt="Hipercard" className="h-6" />;
                                            }

                                            // Cartão genérico caso não identifique a bandeira
                                            return <img src="/images/payment/credit-card.svg" alt="Cartão de Crédito" className="h-6" />;
                                        }
                                        case 'pix':
                                            return <img src="/images/payment/pixIcon.svg" alt="PIX" className="h-6" />;
                                        case 'boleto':
                                            return <img src="/images/payment/boleto.svg" alt="Boleto" className="h-6" />;
                                        default:
                                            return (
                                                <div className="text-sm text-gray-700 dark:text-gray-300">
                                                    {saleData.payment.method || saleData.payment.gateway}
                                                </div>
                                            );
                                    }
                                })()}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">DATA DA TRANSAÇÃO</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{formatDate(saleData.payment.transactionDate)}</p>
                            </div>

                            <div>
                                <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">PARCELAS</h3>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{saleData.payment.installments}</p>
                            </div>

                            {saleData.payment.lastDigits && (
                                <div>
                                    <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">ÚLTIMOS DÍGITOS</h3>
                                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{saleData.payment.lastDigits}</p>
                                </div>
                            )}

                            {saleData.payment.expirationDate && (
                                <div>
                                    <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">DATA DE EXPIRAÇÃO</h3>
                                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{saleData.payment.expirationDate}</p>
                                </div>
                            )}

                            {saleData.payment.cardholderName && (
                                <div>
                                    <h3 className="text-xs font-medium text-gray-500 uppercase dark:text-gray-400">PROPRIETÁRIO</h3>
                                    <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{saleData.payment.cardholderName.toUpperCase()}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bloco de UTMs */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                        <h3 className="text-sm font-medium text-gray-500 uppercase dark:text-gray-400 mb-4">RASTREAMENTO</h3>

                        <div className="space-y-2">
                            <div className="grid grid-cols-3 gap-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 col-span-1">sck</p>
                                <p className="text-sm text-gray-900 dark:text-white col-span-2 break-all whitespace-pre-wrap">{saleData.tracking.sck || '--'}</p>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 col-span-1">utm_source</p>
                                <p className="text-sm text-gray-900 dark:text-white col-span-2 break-all whitespace-pre-wrap">{saleData.tracking.source || '--'}</p>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 col-span-1">utm_medium</p>
                                <p className="text-sm text-gray-900 dark:text-white col-span-2 break-all whitespace-pre-wrap">{saleData.tracking.medium || '--'}</p>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 col-span-1">utm_campaign</p>
                                <p className="text-sm text-gray-900 dark:text-white col-span-2 break-all whitespace-pre-wrap">{saleData.tracking.campaign || '--'}</p>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 col-span-1">utm_content</p>
                                <p className="text-sm text-gray-900 dark:text-white col-span-2 break-all whitespace-pre-wrap">{saleData.tracking.content || '--'}</p>
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 col-span-1">utm_term</p>
                                <p className="text-sm text-gray-900 dark:text-white col-span-2 break-all whitespace-pre-wrap">{saleData.tracking.term || '--'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Componente de notificação */}
            {showNotification && (
                <div className={`fixed top-4 right-4 z-50 rounded-md p-4 shadow-md transition-all duration-300 ${notificationType === "success"
                    ? "bg-success-500 dark:bg-success-400 text-white"
                    : "bg-danger-500 dark:bg-danger-400 text-white"
                    }`}>
                    <div className="flex items-center">
                        {notificationType === "success" ? (
                            <svg
                                className="h-5 w-5 mr-2 text-white dark:text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        ) : (
                            <svg
                                className="h-5 w-5 mr-2 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        )}
                        <span>{notificationMessage}</span>
                    </div>
                </div>
            )}

            {/* Modal de confirmação de reembolso */}
            <Modal
                isOpen={isOpen}
                onClose={closeModal}
                title="Confirmar Reembolso"
                confirmText="Confirmar Reembolso"
                cancelText="Cancelar"
                onConfirm={handleRefund}
                confirmLoading={isRefunding}
                confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
            >
                <div className="py-4">
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                        Tem certeza que deseja reembolsar esta transação?
                    </p>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                        <div className="flex items-start">
                            <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <div>
                                <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                    Atenção, esta ação não pode ser desfeita!
                                </h4>
                                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                                    O valor será devolvido integralmente ao cliente.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
}